import React, { forwardRef, useMemo } from 'react';

import { useGetStreams } from '@vlabs/api/luna-api/client';
import { ControlledOptionsFilter } from '@vlabs/monokit/shared/ui/controlled-options-filter';
import type { Option } from '@vlabs/monokit/shared/ui/options-filter';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { validate } from '@vlabs/shared';

import { useWorkspaceFilters } from '../../useWorkspaceFilters';
import type { VideoAnalyticType } from '../../workspaceFiltersModel';

interface VideoAnalyticsStreamsFilterPopoverProps extends PopoverWithButtonProps {
  analyticType: VideoAnalyticType;
}

function VideoAnalyticsStreamsFilterPopover(
  {
    analyticType,
    ...props
  }: VideoAnalyticsStreamsFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { filters, updateVideoAnalyticsFilters } = useWorkspaceFilters();
  const getStreams = useGetStreams({ page_size: 1000 });
  const options = useMemo<Option[]>(() => getStreams.data?.streams.map((stream) => ({
    label: stream.name ?? stream.stream_id,
    value: stream.stream_id,
  })) ?? [], [getStreams.data]);
  const analytic = filters.videoAnalytics[analyticType];
  const selectedStreams = analytic?.filters?.stream_ids ?? [];

  const setStreams = (streamIds: Option[] | undefined) => {
    updateVideoAnalyticsFilters(analyticType, {
      stream_ids: streamIds,
    });
  };

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={selectedStreams.length === 0}
      onClickReset={() => setStreams(undefined)}
      ref={ref}
    >
      <ControlledOptionsFilter
        isCreatable
        isSearchable
        name={`${analyticType}_stream_ids`}
        onChange={(value) => setStreams(value.length > 0 ? value : undefined)}
        options={options}
        validateProp={validate.UUIDValidator()}
        value={selectedStreams}
      />
    </PopoverWithButton>
  );
}

const VideoAnalyticsStreamsFilterPopoverWithRef = forwardRef(
  VideoAnalyticsStreamsFilterPopover,
);
export {
  VideoAnalyticsStreamsFilterPopoverWithRef as VideoAnalyticsStreamsFilterPopover,
};
